import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';

// MUI Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShieldIcon from "@mui/icons-material/Shield";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SendIcon from "@mui/icons-material/Send";
import LinkIcon from "@mui/icons-material/Link";

// ── Design tokens ─────────────────────────────────────────────────────────────
const card = {
    background: "#ffffff",
    border: "1px solid rgba(59,130,246,0.08)",
    boxShadow: "0 4px 18px rgba(59,130,246,0.05)",
    borderRadius: 20,
    padding: 20,
};

const labelStyle = {
    color: "#64748b",
    fontSize: 11,
    fontFamily: "'DM Sans',sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 8,
    display: "block",
};

const inputStyle = (hasError) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 16px",
    borderRadius: 14,
    border: `1.5px solid ${hasError ? "#ef4444" : "rgba(59,130,246,0.18)"}`,
    background: hasError ? "rgba(239,68,68,0.03)" : "#f8fafc",
    color: "#0f172a",
    fontSize: 14,
    fontFamily: "'DM Sans',sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "monospace",
});

const errMsg = {
    color: "#ef4444",
    fontSize: 11,
    fontFamily: "'DM Sans',sans-serif",
    marginTop: 4,
};

function WalletComponent() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Updating...');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [googleAuth, setGoogleAuth] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [errors, setErrors] = useState({});

    // Wallet address state
    const [usdttrc20wallet, usdttrc20walletSet] = useState(userData?.usdttrc20wallet ?? '');
    const [usdtbep20wallet, usdtbep20walletSet] = useState(userData?.usdtbep20wallet ?? '');
    const [btcwallet, btcwalletSet] = useState(userData?.btcwallet ?? '');
    const [bchwallet, bchwalletSet] = useState(userData?.bchwallet ?? '');
    const [ethwallet, ethwalletSet] = useState(userData?.ethwallet ?? '');
    const [ltcwallet, ltcwalletSet] = useState(userData?.ltcwallet ?? '');
    const [usdcwallet, usdcwalletSet] = useState(userData?.usdcwallet ?? '');
    const [xrpwallet, xrpwalletSet] = useState(userData?.xrpwallet ?? '');
    const [adawallet, adawalletSet] = useState(userData?.adawallet ?? '');
    const [dogewallet, dogewalletSet] = useState(userData?.dogewallet ?? '');
    const [dashwallet, dashwalletSet] = useState(userData?.dashwallet ?? '');
    const [xtzwallet, xtzwalletSet] = useState(userData?.xtzwallet ?? '');
    const [dotwallet, dotwalletSet] = useState(userData?.dotwallet ?? '');
    const [linkwallet, linkwalletSet] = useState(userData?.linkwallet ?? '');
    const [xlmwallet, xlmwalletSet] = useState(userData?.xlmwallet ?? '');
    const [etcwallet, etcwalletSet] = useState(userData?.etcwallet ?? '');
    const [uniwallet, uniwalletSet] = useState(userData?.uniwallet ?? '');
    const [pmwallet, pmwalletSet] = useState(userData?.pmwallet ?? '');
    const [bnbwallet, bnbwalletSet] = useState(userData?.bnbwallet ?? '');

    useEffect(() => {
        userData?.bankdetails?.map((item) => eval(item?.valueJsConstFunction)(item.value));
    }, []);

    const validate = () => {
        const e = {};
        if (!withdrawalPin) e.withdrawalPin = 'Security PIN is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const doAddressChange = (ev) => {
        ev.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setLoadingText('Updating wallet details...');

        dispatch(authActions.onLogin(`account/account_change/1`, {
            usdttrc20wallet, usdtbep20wallet, btcwallet, bchwallet, ethwallet, ltcwallet,
            usdcwallet, xrpwallet, adawallet, dogewallet, dashwallet, xtzwallet,
            dotwallet, linkwallet, xlmwallet, etcwallet, uniwallet, pmwallet, bnbwallet,
            withdrawalPin, googleAuth,
        }, response => {
            notify(response.msg, response.code);
            setLoading(false);
            if (response.code == 1) {
                setWithdrawalPin('');
                setErrors({});
            }
        }));
    };

    // Count how many wallets are already bound
    const boundCount = userData?.bankdetails?.filter(item => {
        try { return !!eval(item.valueJsConst); } catch { return false; }
    }).length ?? 0;
    const totalCount = userData?.bankdetails?.length ?? 0;

    return (
        <SiteWide id="main" title="Account Wallet">
            <Section loading={loading} overlayText={loadingText} title="Account Wallet">
                <form onSubmit={doAddressChange}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 32 }}>

                        {/* ── Hero gradient card ───────────────────────────────── */}
                        <div style={{
                            borderRadius: 22, padding: "22px 20px",
                            position: "relative", overflow: "hidden",
                            background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                            boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
                        }}>
                            {/* glow blob */}
                            <div style={{
                                position: "absolute", top: -30, right: -30,
                                width: 160, height: 160, borderRadius: "50%",
                                background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                                pointerEvents: "none",
                            }} />

                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                <div>
                                    <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                        Wallet Management
                                    </p>
                                    <p style={{ color: "#fff", fontSize: 24, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "6px 0 0", lineHeight: 1.2 }}>
                                        Crypto Addresses
                                    </p>
                                    <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: "4px 0 0" }}>
                                        Bind your withdrawal wallets below
                                    </p>
                                </div>
                                <div style={{
                                    width: 48, height: 48, borderRadius: "50%",
                                    background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <AccountBalanceWalletIcon style={{ color: "#fff", fontSize: 24 }} />
                                </div>
                            </div>

                            {/* Bound wallet progress bar */}
                            <div style={{ marginTop: 18 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                        Wallets Bound
                                    </span>
                                    <span style={{ color: "#fff", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>
                                        {boundCount} / {totalCount}
                                    </span>
                                </div>
                                <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.18)" }}>
                                    <div style={{
                                        height: "100%", borderRadius: 99,
                                        width: totalCount > 0 ? `${(boundCount / totalCount) * 100}%` : "0%",
                                        background: "linear-gradient(90deg,#86efac,#22c55e)",
                                        transition: "width 0.6s ease",
                                    }} />
                                </div>
                            </div>
                        </div>

                        {/* ── Wallet address inputs ────────────────────────────── */}
                        {userData?.bankdetails?.map((item, index) => {
                            let walletValue, setWalletValue;
                            try {
                                walletValue = eval(item.valueJsConst);
                                setWalletValue = eval(item.valueJsConstFunction);
                            } catch { walletValue = ''; setWalletValue = () => { }; }

                            const isBound = !!walletValue;

                            return (
                                <div key={index} style={{ ...card }}>
                                    {/* header row */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                                        <div style={{
                                            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                            background: isBound
                                                ? "linear-gradient(135deg,#16a34a,#22c55e)"
                                                : "linear-gradient(135deg,#1e3a8a,#2563eb)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            boxShadow: isBound
                                                ? "0 4px 12px rgba(34,197,94,0.25)"
                                                : "0 4px 12px rgba(37,99,235,0.2)",
                                        }}>
                                            <MonetizationOnIcon style={{ color: "#fff", fontSize: 18 }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a", fontFamily: "'Syne',sans-serif" }}>
                                                {item.abbr} Wallet
                                            </p>
                                            {isBound && (
                                                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                                                    <CheckCircleOutlineIcon style={{ fontSize: 11, color: "#16a34a" }} />
                                                    <span style={{ fontSize: 10, color: "#16a34a", fontFamily: "'DM Sans',sans-serif" }}>Bound</span>
                                                </div>
                                            )}
                                        </div>
                                        {isBound && (
                                            <span style={{
                                                fontSize: 10, fontFamily: "monospace",
                                                color: "#64748b", background: "rgba(59,130,246,0.05)",
                                                border: "1px solid rgba(59,130,246,0.1)",
                                                borderRadius: 8, padding: "3px 8px",
                                            }}>
                                                {walletValue.substring(0, 6)}…{walletValue.slice(-4)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Input */}
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="text"
                                            value={walletValue}
                                            onChange={e => setWalletValue(e.target.value)}
                                            placeholder={`Enter ${item.abbr} wallet address`}
                                            style={inputStyle(false)}
                                        />
                                        <LinkIcon style={{
                                            position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                                            fontSize: 16, color: "#94a3b8",
                                        }} />
                                    </div>
                                </div>
                            );
                        })}

                        {/* ── Security PIN card ────────────────────────────────── */}
                        <div style={card}>
                            <span style={labelStyle}>Security PIN</span>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showPin ? "text" : "password"}
                                    value={withdrawalPin}
                                    onChange={e => { setWithdrawalPin(e.target.value); setErrors(p => ({ ...p, withdrawalPin: null })); }}
                                    maxLength={6}
                                    placeholder="Enter your PIN"
                                    style={{ ...inputStyle(!!errors.withdrawalPin), paddingRight: 52, fontFamily: "'DM Sans',sans-serif" }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPin(!showPin)}
                                    style={{
                                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                                        background: "none", border: "none", cursor: "pointer", padding: 0,
                                        display: "flex", alignItems: "center",
                                    }}
                                >
                                    {showPin
                                        ? <VisibilityOffIcon style={{ color: "#94a3b8", fontSize: 18 }} />
                                        : <VisibilityIcon style={{ color: "#94a3b8", fontSize: 18 }} />
                                    }
                                </button>
                            </div>
                            {errors.withdrawalPin && <p style={errMsg}>{errors.withdrawalPin}</p>}

                            {/* Shield note */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
                                <ShieldIcon style={{ fontSize: 13, color: "#22c55e" }} />
                                <span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                    Your PIN is encrypted and never stored in plain text
                                </span>
                            </div>
                        </div>

                        {/* ── Security notice ──────────────────────────────────── */}
                        <div style={{
                            ...card,
                            background: "rgba(59,130,246,0.04)",
                            border: "1px solid rgba(59,130,246,0.12)",
                            padding: "14px 18px",
                        }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                <InfoOutlinedIcon style={{ color: "#2563eb", fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                                <p style={{ margin: 0, fontSize: 12, color: "#334155", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                                    All wallet addresses are encrypted with industry-standard security. Only bind wallets you own.
                                    Changes require your Security PIN to prevent unauthorized modifications.
                                </p>
                            </div>
                        </div>

                        {/* ── Submit button ────────────────────────────────────── */}
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: "100%", padding: "16px 0", borderRadius: 16, border: "none",
                                background: loading ? "rgba(59,130,246,0.3)" : "linear-gradient(135deg,#1e3a8a,#2563eb)",
                                color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                cursor: loading ? "not-allowed" : "pointer", outline: "none",
                                boxShadow: loading ? "none" : "0 6px 20px rgba(37,99,235,0.3)",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                                transition: "all 0.2s",
                            }}
                        >
                            {loading ? (
                                <>
                                    <div style={{
                                        width: 18, height: 18,
                                        border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                                        borderRadius: "50%", animation: "spin 0.8s linear infinite",
                                    }} />
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <SaveIcon style={{ fontSize: 18 }} />
                                    Save Wallet Details
                                </>
                            )}
                        </button>

                    </div>
                </form>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Section>
        </SiteWide>
    );
}

export default WalletComponent;